import React, { useEffect } from 'react';
import { Header } from '@/components/layout/Header';
import { useDocumentProcessing } from '@/domain/document-processing/hooks/use-document-processing';
import { ProcessingResults } from '@/components/ui/processing-results';
import { Button } from '@/components/ui/button';
import { NewOrderHome } from '@/components/ui/new-order-home';
import { useProductOrderStore, OrderNotFoundError } from '@/domain/product-orders/stores/useProductOrderStore';
import { useUser, useAuth } from '@clerk/clerk-react';
import { checkUploads } from '@/domain/document-processing/utils/document-validation';
import { toast } from 'sonner';
import { Loader2, History } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { OrderFileSection } from '@/components/ui/order-file-section';
import { OrderHeader } from '@/components/ui/order-header';
import { useDocumentStore } from '@/stores/useDocumentStore';
import { OrdersPopup } from '@/components/ui/orders-popup';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ProductOrderService } from '@/domain/product-orders/services/ProductOrderService';
import * as Sentry from '@sentry/react';

export default function Index() {
  const { processDocuments, restoreOrder, isWatchingOrder } = useDocumentProcessing();
  const { user } = useUser();
  const { getToken } = useAuth();
  const { createOrder, currentOrder } = useProductOrderStore();
  const [pendingUpload, setPendingUpload] = React.useState<{ orderId: string; files: File[] } | null>(null);
  const [isRestoringOrder, setIsRestoringOrder] = React.useState(false);
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const [exportTarget, setExportTarget] = React.useState<HTMLDivElement | null>(null);
  const { results, isProcessing } = useDocumentStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const { key: locationKey } = useLocation();
  const requestedOrderId = searchParams.get('order');
  const restoreRequest = React.useRef(0);

  // This effect updates selectedFiles whenever document store results change
  // This allows file display to work when loading a historical order
  useEffect(() => {
    if (results.length > 0) {
      // Convert document result models to File objects for display
      const filesFromResults = results.map(result => {
        // Create a blob with minimal content
        const blob = new Blob([''], { type: result.fileType || 'application/octet-stream' });
        
        // Create a File object from the blob
        const file = new File([blob], result.fileName || 'document.pdf', {
          type: result.fileType || 'application/octet-stream'
        });
        
        // Add ALL document metadata as custom properties for display
        Object.defineProperties(file, {
          documentId: { value: result.documentId, writable: true },
          documentCategory: { value: result.documentCategory, writable: true },
          isHistorical: { value: true, writable: true },
          // Add all document type specific metadata
          documentMetadata: { value: result.documentMetadata, writable: true },
          documentSpecificDetails: { value: result.documentSpecificDetails, writable: true },
          transactionDetails: { value: result.transactionDetails, writable: true },
          shippingDetails: { value: result.shippingDetails, writable: true }
        });
        
        return file;
      });
      
      if (filesFromResults.length > 0) {
        setSelectedFiles(filesFromResults);
      }
    }
  }, [results]);

  // Only an explicit order URL restores a view. A bare /app always starts fresh.
  useEffect(() => {
    const request = ++restoreRequest.current;
    if (useDocumentStore.getState().isProcessing && currentOrder && requestedOrderId !== currentOrder.id) {
      setSearchParams({ order: currentOrder.id }, { replace: true });
      return;
    }
    if (!requestedOrderId) {
      useDocumentStore.getState().reset();
      useProductOrderStore.getState().setCurrentOrder(null);
      setSelectedFiles([]);
      setIsRestoringOrder(false);
      return;
    }
    if (currentOrder?.id === requestedOrderId && useDocumentStore.getState().isProcessing && isWatchingOrder(requestedOrderId)) return;
    // A newly created order is about to upload. Every explicit reopening,
    // including the same URL or a page remount, must reconnect to its run.
    if (!user || pendingUpload?.orderId === requestedOrderId) return;
    setIsRestoringOrder(true);
    setSelectedFiles([]);
    useProductOrderStore.getState().setCurrentOrder(null);
    useDocumentStore.getState().reset();
    void Sentry.startSpan({ name: 'Open order', op: 'order.open', attributes: { source: 'url' } }, async () => {
      const order = await Sentry.startSpan({ name: 'Fetch order', op: 'order.open.step' }, () => ProductOrderService.getOrderById(requestedOrderId));
      if (!order) throw new OrderNotFoundError(requestedOrderId);
      if (request !== restoreRequest.current) return;
      const token = await Sentry.startSpan({ name: 'Get sign-in token', op: 'order.open.step' }, () => getToken());
      if (request !== restoreRequest.current) return;
      if (!token) throw new Error('No sign-in token');
      await restoreOrder(order, token, () => request === restoreRequest.current);
    }).catch(() => {
      if (request !== restoreRequest.current) return;
      useProductOrderStore.getState().setCurrentOrder(null);
      // The page now shows a new order, so drop the order that failed from the URL.
      setSearchParams({}, { replace: true });
      toast.error('Could not load this order. You can retry from order history.');
    }).finally(() => {
      if (request === restoreRequest.current) setIsRestoringOrder(false);
    });
    return () => { restoreRequest.current++; };
  }, [requestedOrderId, user?.id, locationKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartVerification = async (files: File[], reference: string) => {
    if (!user) throw new Error('Sign in to create an order');
    const order = await createOrder(user.id, "bypass", null, reference);
    setSearchParams({ order: order.id });
    useDocumentStore.getState().reset();
    setSelectedFiles(files);
    setPendingUpload({ orderId: order.id, files });
  };

  // Wait for the processing hook to render with the newly created order ID.
  // Consuming the queue before starting also prevents duplicate submissions.
  const startedUpload = React.useRef<typeof pendingUpload>(null);
  useEffect(() => {
    if (!pendingUpload || currentOrder?.id !== pendingUpload.orderId || startedUpload.current === pendingUpload) return;
    startedUpload.current = pendingUpload;
    setPendingUpload(null);
    void processDocuments(pendingUpload.files).catch(() => {
      toast.error('Could not process your documents. Please try uploading them again.');
    });
  }, [pendingUpload, currentOrder?.id, processDocuments]);

  const handleNewOrder = () => {
    if (isProcessing || pendingUpload) return;
    restoreRequest.current++;
    setSearchParams({});
    useDocumentStore.getState().reset();
    useProductOrderStore.getState().setCurrentOrder(null);
    setSelectedFiles([]);
    setPendingUpload(null);
  };

  const handleFilesSelected = async (files: FileList) => {
    const state = useDocumentStore.getState();
    if (state.isProcessing || state.results.length > 0 || state.consolidationResult || pendingUpload) return;
    const { accepted, rejected } = await checkUploads(Array.from(files));
    for (const { file, reason } of rejected) {
      toast.error(`${file.name} can't be uploaded`, { description: reason, duration: 10000 });
    }
    if (accepted.length > 0 && !useDocumentStore.getState().isProcessing) {
      setSelectedFiles((prev) => [...prev, ...accepted]);
      await processDocuments(accepted);
    }
  };

  // Only allow removing non-historical files 
  const handleRemoveFile = (file: File) => {
    const state = useDocumentStore.getState();
    if (state.isProcessing || state.results.length > 0 || state.consolidationResult) return;
    if ((file as any).isHistorical) {
      toast.warning("Cannot remove files from historical orders");
      return;
    }
    setSelectedFiles((prev) => prev.filter(f => f !== file));
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="container mx-auto px-4 py-6 flex-grow">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            {requestedOrderId && currentOrder ? <h1 className="text-2xl font-bold">Screening report</h1> : <span className="text-sm text-muted-foreground">New order</span>}
            <OrdersPopup 
              trigger={
                <Button variant="outline" size="sm" className="gap-2">
                  <History className="h-4 w-4" />
                  Order History
                </Button>
              }
            />
          </div>
          <AnimatePresence mode="wait">
            {isRestoringOrder || !requestedOrderId || !currentOrder ? (
              isRestoringOrder ? (
                <div role="status" className="flex items-center justify-center gap-3 py-20">
                  <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin motion-reduce:animate-none" />
                  Loading your order…
                </div>
              ) : <NewOrderHome onStart={handleStartVerification} />
            ) : (
              <motion.div
                key={currentOrder.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-4"
              >
                <OrderHeader
                  orderNumber={currentOrder?.order_number || ''}
                  manualOrderNumber={currentOrder?.manual_order_number || ''}
                  onNewOrder={handleNewOrder}
                  exportRef={setExportTarget}
                />
                <ProcessingResults
                  exportTarget={exportTarget}
                  documents={<OrderFileSection
                    files={selectedFiles}
                    onRemoveFile={handleRemoveFile}
                    onFilesSelected={handleFilesSelected}
                  />}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
